import {isEqual} from 'lodash';

/**
 * Provides a higher level API around subscribing to a redux store.
 *
 * @param isEqual - the equality function to use when detecting changes
 * @param store - the root redux data store
 * @param selectFields - a function that takes the entire application state as an object, and returns the portion to be watched
 * @param onChange - a function which will be called when a change in the subtree is detected.  Will be passed (oldState, newState)
 *
 * Usage:
 * Watcher(
 *   (a, b) => a === b   // Equality check function
 * )(
 *   reduxStore,
 *   state => state.theSubtreeIWantToSubscribeTo,
 * )(
 *   (oldState, newState) => { console.log("the state has changed") }
 * )
 *
 */
export function Watcher(isEqual) {
  return (store, selectFields) => onChange => {

    let currentState = selectFields(store.getState());
    const checkForChange = () => {

      const previous = currentState;
      currentState = selectFields(store.getState());
      if (!isEqual(previous, currentState)) {
        onChange(previous, currentState);
      }
    };

    let unsubscribe;
    const self = {
      on() {
        unsubscribe = store.subscribe(checkForChange)
      },

      off() {
        unsubscribe && unsubscribe();
        unsubscribe = null;
      },

      getCurrentState() {
        return currentState
      },
    };
    self.on();
    return self;
  };
}

/**
 * Returns a watcher curried with a deep equality function.
 * Usage:
 *
 * let selectFields = (state) => state.thePartICareAbout
 * let onChange = (oldState, newState) => { console.log("The state has changed from: ", oldState, " to: ", newState) }
 *
 * let stateWatcher = Watcher.deepEquals(rootReduxStore, selectFields)(onChange);
 *
 * stateWatcher.getCurrentState()    // Get the current state, processed via selectFields
 * stateWatcher.off()                // Stop watching
 *
 */
Watcher.deepEquals = Watcher(isEqual);

/**
 * Same as deepEquals, with a different equality checker.
 */
Watcher.strictEquals = Watcher((a, b) => a === b);
